class MissionCarousel extends HTMLElement {
  connectedCallback() {
    this._track     = this.querySelector('.mission__photos');
    this._origCards = Array.from(this._track.querySelectorAll('.mission__photo'));
    this._count     = this._origCards.length;

    if (this._count === 0) return;

    this._buildClones();
    this._allCards  = Array.from(this._track.querySelectorAll('.mission__photo'));
    this._activeIdx = this._count + Math.floor(this._count / 2);
    this._rafId     = null;

    this._bindEvents();

    requestAnimationFrame(() => {
      this._goTo(this._activeIdx, false);
      this._updateScales();
      this.setAttribute('data-initialized', '');
    });
  }

  disconnectedCallback() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _buildClones() {
    const makeClones = () =>
      this._origCards.map((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        return clone;
      });

    // Prepend in reverse so they arrive in correct visual order
    makeClones()
      .reverse()
      .forEach((c) => this._track.prepend(c));

    makeClones().forEach((c) => this._track.append(c));
  }

  _goTo(idx, smooth) {
    const card = this._allCards[idx];
    if (!card) return;
    const target =
      card.offsetLeft + card.offsetWidth / 2 - this._track.offsetWidth / 2;
    if (smooth) {
      this._track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    } else {
      this._track.scrollLeft = Math.max(0, target);
    }
  }

  _nearestIndex() {
    const viewCenter = this._track.scrollLeft + this._track.offsetWidth / 2;
    let nearest = 0;
    let minDist = Infinity;
    this._allCards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft + card.offsetWidth / 2 - viewCenter);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  }

  _handleLoop() {
    const idx = this._nearestIndex();
    let target = null;

    if (idx < this._count) {
      target = idx + this._count;
    } else if (idx >= this._count * 2) {
      target = idx - this._count;
    }

    if (target !== null) {
      this._activeIdx = target;
      this._track.style.scrollSnapType = 'none';
      this._goTo(target, false);
      requestAnimationFrame(() => {
        this._track.style.scrollSnapType = '';
      });
    } else {
      this._activeIdx = idx;
      // Restore mandatory snap that _shift disabled for its smooth scroll. We're
      // already resting on the snap point, so this can't cause a jump.
      this._track.style.scrollSnapType = '';
    }
  }

  _shift(dir) {
    const nearest = this._nearestIndex();
    const target  = nearest + dir;
    if (target < 0 || target >= this._allCards.length) return;
    this._activeIdx = target;
    // Free the smooth scroll from mandatory snap. With scroll-snap-type: x
    // mandatory active, Blink halts a programmatic scrollTo({ behavior: 'smooth' })
    // a few pixels short of the true snap point, leaving the centered card
    // off-center (left card fully visible, right card clipped). Snap is restored
    // on settle in _handleLoop, by which point we're resting exactly on the snap
    // point, so the restore can't cause a jump.
    this._track.style.scrollSnapType = 'none';
    this._goTo(target, true);
  }

  _updateScales() {
    const viewCenter = this._track.scrollLeft + this._track.offsetWidth / 2;
    const maxDist    = this._track.offsetWidth * 0.55;
    // Falloff and origin-pull are breakpoint-driven via CSS vars: gentle +
    // center-origin on mobile/tablet, steeper + pull-toward-center on desktop
    // so the scaled side cards sit tight against the hero card (Figma cluster).
    const styles  = getComputedStyle(this);
    const falloff = parseFloat(styles.getPropertyValue('--mission-coverflow-falloff')) || 0.2;
    const pull    = parseFloat(styles.getPropertyValue('--mission-coverflow-pull')) || 0;

    this._allCards.forEach((card) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const dist  = Math.abs(cardCenter - viewCenter);
      const t     = Math.min(dist / maxDist, 1);
      card.style.setProperty('--scale', (1 - t * falloff).toFixed(3));

      if (pull) {
        // Scale side cards from their inner edge so they hug the centered hero.
        card.style.transformOrigin =
          cardCenter < viewCenter - 4 ? 'right center'
          : cardCenter > viewCenter + 4 ? 'left center'
          : 'center center';
      } else {
        card.style.transformOrigin = '';
      }
    });
  }

  _bindEvents() {
    this.querySelector('.mission__arrow--prev')?.addEventListener('click', () =>
      this._shift(-1)
    );
    this.querySelector('.mission__arrow--next')?.addEventListener('click', () =>
      this._shift(1)
    );

    this._track.addEventListener(
      'scroll',
      () => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._updateScales();
        });
      },
      { passive: true }
    );

    const onSettle = () => this._handleLoop();

    if ('onscrollend' in window) {
      this._track.addEventListener('scrollend', onSettle, { passive: true });
    } else {
      let timer;
      this._track.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(onSettle, 200);
        },
        { passive: true }
      );
    }
  }
}

if (!customElements.get('mission-carousel')) {
  customElements.define('mission-carousel', MissionCarousel);
}
